/**
 * Shared EWSS (Early Warning Scoring System) calculator — adult / NEWS2-based.
 * Single source of truth for the frontend; mirrors backend App\Services\EwssCalculator.
 *
 * Dipakai oleh: Monitoring IGD, Monitoring Transport Eksternal, Monitoring Transfusi.
 * Skema warna standar RSPS: normal/ringan = hijau, sedang = amber, tinggi = merah, blue = biru.
 */

export interface EwssParams {
  respirasi: number;
  spo2: number;
  hasO2Support: boolean;
  tdSistolik: number;
  nadi: number;
  kesadaran: "A" | "V" | "P" | "U";
  suhu: number;
}

export interface EwssResult {
  scores: Record<string, number>;
  total: number;
  hasBlue: boolean;
  kategori: string;
}

export function skorRespirasi(rr: number): number {
  if (rr >= 25 || rr <= 8) return 3;
  if (rr >= 21) return 2;
  if (rr >= 12) return 0;
  if (rr >= 9) return 1;
  return 0;
}

export function skorSpo2(spo2: number): number {
  if (spo2 <= 91) return 3;
  if (spo2 <= 93) return 2;
  if (spo2 <= 95) return 1;
  if (spo2 >= 96) return 0;
  return 0;
}

export function skorSistolik(sistole: number): number {
  if (sistole >= 220 || sistole <= 90) return 3;
  if (sistole >= 111) return 0;
  if (sistole >= 101) return 1;
  if (sistole >= 91) return 2;
  return 0;
}

export function skorNadi(nadi: number): number {
  if (nadi >= 131 || nadi <= 40) return 3;
  if (nadi >= 111) return 2;
  if (nadi >= 91 || nadi <= 50) return 1;
  if (nadi >= 51) return 0;
  return 0;
}

export function skorSuhu(suhu: number): number {
  if (suhu <= 35.0) return 3;
  if (suhu > 39.0) return 2;
  if (suhu >= 36.1 && suhu <= 38.0) return 0;
  if (suhu > 35.0 || suhu > 38.0) return 1;
  return 0;
}

export function skorKesadaran(avpu: string): number {
  const upper = avpu.toUpperCase().trim();
  if (upper === "A") return 0;
  if (["V", "P", "U"].includes(upper)) return 3;
  return 0;
}

export function skorSuplemenO2(hasSupport: boolean): number {
  return hasSupport ? 2 : 0;
}

export function calculateEwss(params: EwssParams): EwssResult {
  const scores: Record<string, number> = {
    respirasi: skorRespirasi(params.respirasi),
    spo2: skorSpo2(params.spo2),
    suplemen_o2: skorSuplemenO2(params.hasO2Support),
    td_sistolik: skorSistolik(params.tdSistolik),
    nadi: skorNadi(params.nadi),
    kesadaran: skorKesadaran(params.kesadaran),
    suhu: skorSuhu(params.suhu),
  };

  const total = Object.values(scores).reduce((sum, s) => sum + s, 0);
  const hasBlue = Object.values(scores).some(s => s === 3);

  let kategori = "normal";
  if (hasBlue && total >= 7) kategori = "blue";
  else if (total >= 7) kategori = "tinggi";
  else if (total >= 5) kategori = "sedang";
  else if (total >= 1) kategori = "ringan";

  return { scores, total, hasBlue, kategori };
}

export function getEwssColor(kategori: string): string {
  const colors: Record<string, string> = {
    normal: "green",
    ringan: "green",
    sedang: "amber",
    tinggi: "red",
    blue: "blue",
  };
  return colors[kategori] || "grey";
}

export function getEwssRekomendasi(kategori: string): string {
  const rekom: Record<string, string> = {
    normal: "Lanjutkan monitoring rutin",
    ringan: "Tingkatkan frekuensi monitoring",
    sedang: "Eskalasi - Konsul DPJP",
    tinggi: "KRITIS - Aktivasi tim sekunder",
    blue: "KRITIS BLUE - Aktivasi Code Blue",
  };
  return rekom[kategori] || "";
}

/**
 * Map EWSS category to a Vuetify v-alert type for live preview.
 */
export function getEwssAlertType(kategori: string | null | undefined): string {
  if (!kategori) return "info";
  if (kategori === "tinggi" || kategori === "blue") return "error";
  if (kategori === "sedang") return "warning";
  return "success";
}

/**
 * Map kesadaran kualitatif (CM/somnolen/apatis/delirium/sopor/koma) ke AVPU
 * untuk keperluan scoring EWSS. Hanya Compos Mentis = Alert (skor 0),
 * selain itu dianggap "tidak alert" (skor 3), sesuai konsep AVPU NEWS2.
 */
export function kesadaranToAvpu(kesadaran: string | null | undefined): "A" | "V" | "P" | "U" {
  if (!kesadaran) return "A";
  const k = kesadaran.toLowerCase().trim();
  if (k === "cm" || k === "compos mentis" || k === "a") return "A";
  if (k === "koma" || k === "u") return "U";
  if (k === "sopor" || k === "p") return "P";
  return "V";
}

export function calculateMap(sistolik: number, diastolik: number): number {
  return Math.round(diastolik + (sistolik - diastolik) / 3);
}
